import { pushSubscriptionSchema, type PushSubscriptionInput } from "@tm/contracts";

/** Served from `public/`; it shows pushed notifications and opens the app when one is clicked. */
const SERVICE_WORKER_URL = "/sw.js";

/** A failure the user can act on; its message is safe to show. */
export class PushSetupError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PushSetupError";
  }
}

export function isPushSupported(): boolean {
  return (
    window.isSecureContext &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function notificationPermission(): NotificationPermission {
  return isPushSupported() ? Notification.permission : "default";
}

/** VAPID keys are URL-safe base64; the Push API wants the raw bytes. */
function decodeBase64Url(value: string): Uint8Array<ArrayBuffer> {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "="));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function sameBytes(a: ArrayBuffer | null, b: Uint8Array): boolean {
  if (a?.byteLength !== b.byteLength) return false;
  const view = new Uint8Array(a);
  return view.every((byte, index) => byte === b[index]);
}

/** This browser's subscription, if it has one. Never registers the service worker. */
export async function getPushSubscription(): Promise<PushSubscription | null> {
  if (!isPushSupported()) return null;
  const registration = await navigator.serviceWorker.getRegistration("/");
  return registration ? registration.pushManager.getSubscription() : null;
}

/**
 * Registers the service worker and subscribes with the server's key. An existing subscription
 * is reused, unless it was made with another key (the server's keys were rotated).
 */
export async function createPushSubscription(publicKey: string): Promise<PushSubscription> {
  try {
    await navigator.serviceWorker.register(SERVICE_WORKER_URL, { scope: "/" });
    const registration = await navigator.serviceWorker.ready;
    const key = decodeBase64Url(publicKey);
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
      if (sameBytes(existing.options.applicationServerKey, key)) return existing;
      await existing.unsubscribe();
    }
    return await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: key,
    });
  } catch {
    throw new PushSetupError("This browser could not set up push notifications.");
  }
}

export function toSubscriptionInput(subscription: PushSubscription): PushSubscriptionInput {
  const parsed = pushSubscriptionSchema.safeParse(subscription.toJSON());
  if (!parsed.success) {
    throw new PushSetupError("This browser returned a push subscription the app cannot use.");
  }
  return parsed.data;
}

/** Signing out: this browser should stop receiving the previous account's notifications. */
export async function removeBrowserPushSubscription(): Promise<void> {
  try {
    await (await getPushSubscription())?.unsubscribe();
  } catch {
    // Best effort: the server forgets the subscription with the session anyway.
  }
}
